<template>
  <div class="w-full">
    <!-- Loading -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <LoadingComponent />
    </div>

    <!-- Classificação -->
    <div v-else-if="standings && standings.standings" class="bg-white rounded-lg shadow-md overflow-hidden">
      <!-- Header -->
      <div class="text-black px-6 py-4">
        <h2 class="text-2xl font-bold">📊 Classificação do Brasileiro</h2>
        <p class="text-sm mt-1">
          {{ standings.leagueName }} - {{ standings.season }}
        </p>
      </div>

      <!-- Legenda -->
      <div class="px-6 py-3 bg-gray-50 border-b text-xs flex flex-wrap gap-4">
        <div class="flex items-center">
          <div class="w-3 h-3 bg-blue-500 mr-1"></div>
          <span>Libertadores</span>
        </div>
        <div class="flex items-center">
          <div class="w-3 h-3 bg-orange-500 mr-1"></div>
          <span>Sul-Americana</span>
        </div>
        <div class="flex items-center">
          <div class="w-3 h-3 bg-red-500 mr-1"></div>
          <span>Rebaixamento</span>
        </div>
      </div>

      <!-- Tabela Desktop -->
      <div class="hidden lg:block overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-100 border-b-2 border-gray-300">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Pos</th>
              <th class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Time</th>
              <th class="px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase">P</th>
              <th class="px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase">J</th>
              <th class="px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase">V</th>
              <th class="px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase">E</th>
              <th class="px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase">D</th>
              <th class="px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase">GP</th>
              <th class="px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase">GC</th>
              <th class="px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase">SG</th>
              <th class="px-2 py-3 text-center text-xs font-semibold text-gray-700 uppercase">%</th>
              <th class="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Últimos 5</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="team in standings.standings" 
              :key="team.team.id"
              :class="[
                'border-b hover:bg-gray-50 transition-colors',
                getPositionClass(team.description)
              ]">
              <!-- Posição -->
              <td class="px-4 py-3">
                <div class="flex items-center">
                  <span class="font-bold text-gray-700">{{ team.rank }}</span>
                  <span class="ml-2 text-sm">{{ getStatusIcon(team.status) }}</span>
                </div>
              </td>
              
              <!-- Time -->
              <td class="px-4 py-3">
                <div class="flex items-center">
                  <img 
                    :src="team.team.logo" 
                    :alt="team.team.name"
                    @error="handleImageError"
                    class="h-6 w-6 object-contain mr-3" />
                  <span class="font-medium text-gray-900">{{ team.team.name }}</span>
                </div>
              </td>
              
              <!-- Pontos -->
              <td class="px-2 py-3 text-center">
                <span class="font-bold text-green-600">{{ team.points }}</span>
              </td>
              
              <!-- Jogos -->
              <td class="px-2 py-3 text-center text-gray-700">{{ team.all.played }}</td>
              
              <!-- Vitórias -->
              <td class="px-2 py-3 text-center text-gray-700">{{ team.all.win }}</td>
              
              <!-- Empates -->
              <td class="px-2 py-3 text-center text-gray-700">{{ team.all.draw }}</td>
              
              <!-- Derrotas -->
              <td class="px-2 py-3 text-center text-gray-700">{{ team.all.lose }}</td>
              
              <!-- Gols Pro -->
              <td class="px-2 py-3 text-center text-gray-700">{{ team.all.goals.for }}</td>
              
              <!-- Gols Contra -->
              <td class="px-2 py-3 text-center text-gray-700">{{ team.all.goals.against }}</td>
              
              <!-- Saldo de Gols -->
              <td class="px-2 py-3 text-center">
                <span :class="team.goalsDiff > 0 ? 'text-green-600' : team.goalsDiff < 0 ? 'text-red-600' : 'text-gray-700'">
                  {{ team.goalsDiff > 0 ? '+' : '' }}{{ team.goalsDiff }}
                </span>
              </td>
              
              <!-- Aproveitamento -->
              <td class="px-2 py-3 text-center text-gray-700 text-sm">
                {{ calculatePerformance(team.points, team.all.played) }}
              </td>
              
              <!-- Form (últimos 5 jogos) -->
              <td class="px-4 py-3 text-center">
                <span class="text-lg">{{ formatForm(team.form) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Cards Mobile -->
      <div class="lg:hidden">
        <div 
          v-for="team in standings.standings" 
          :key="team.team.id"
          :class="[
            'border-b hover:bg-gray-50 transition-colors',
            getPositionClass(team.description)
          ]">
          <!-- Linha única compacta -->
          <div class="flex items-center justify-between py-3 px-4">
            <!-- Posição -->
            <div class="flex items-center justify-center w-10">
              <span class="font-bold text-lg text-gray-700">{{ team.rank }}</span>
            </div>
            
            <!-- Logo -->
            <div class="flex items-center justify-center w-10 mx-2">
              <img 
                :src="team.team.logo" 
                :alt="team.team.name"
                @error="handleImageError"
                class="h-8 w-8 object-contain" />
            </div>
            
            <!-- Nome do Time -->
            <div class="flex-1 min-w-0 mx-2">
              <div class="font-medium text-gray-900 truncate">{{ team.team.name }}</div>
            </div>
            
            <!-- Pontos -->
            <div class="flex items-center justify-end w-16">
              <span class="font-bold text-xl text-green-600">{{ team.points }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 bg-gray-50 text-xs text-gray-500 text-center">
        Última atualização: {{ formatDate(standings.updatedAt) }}
      </div>
    </div>

    <!-- Erro -->
    <div v-else class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
      <p class="text-red-600">Erro ao carregar classificação</p>
    </div>
  </div>
</template>

<script>
import StandingsService from '@/services/standingsService';
import LoadingComponent from './LoadingComponent.vue';

export default {
  name: 'StandingsTable',
  components: {
    LoadingComponent
  },
  props: {
    roundNumber: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      standings: null,
      loading: true,
      error: null
    };
  },
  mounted() {
    this.loadStandings();
  },
  watch: {
    roundNumber() {
      this.loadStandings();
    }
  },
  methods: {
    async loadStandings() {
      this.loading = true;
      this.error = null;

      try {
        if (this.roundNumber) {
          this.standings = await StandingsService.getStandingsByRound(this.roundNumber);
        } else {
          this.standings = await StandingsService.getCurrentStandings();
        }
      } catch (error) {
        console.error('Erro ao carregar classificação:', error);
        this.error = error;
      } finally {
        this.loading = false;
      }
    },

    formatForm(form) {
      return StandingsService.formatForm(form);
    },

    getPositionClass(description) {
      return StandingsService.getPositionClass(description);
    },

    getStatusIcon(status) {
      return StandingsService.getStatusIcon(status);
    },

    calculatePerformance(points, played) {
      return StandingsService.calculatePerformance(points, played);
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('pt-BR');
    },

    handleImageError(event) {
      event.target.src = require('@/assets/images/default-team-logo.svg');
    }
  }
};
</script>

<style scoped>
/* Animação suave ao hover */
tr:hover {
  transform: scale(1.001);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Responsive table */
@media (max-width: 1024px) {
  table {
    font-size: 0.875rem;
  }
}
</style>
